import React from 'react'
import { useSelector } from 'react-redux'
import { selectEquipment, selectCharacter } from '../inventorySlice'
import { useRarities } from '../../../../hooks/useRarities'

const ItemTooltip = ({ item, position, sourceType, slotType }) => {
  const equipment = useSelector(selectEquipment)
  const character = useSelector(selectCharacter)
  const { getRarityColor, getRarityDisplayName } = useRarities()

  if (!item) return null

  const getSlotName = (type) => {
    const slotNames = {
      head: 'Cabeza',
      chest: 'Pecho',
      hands: 'Manos',
      legs: 'Piernas',
      feet: 'Pies',
      mainHand: 'Mano Principal',
      offHand: 'Mano Secundaria',
      weapon: 'Arma',
      armor: 'Armadura',
      consumable: 'Consumible',
      misc: 'Varios',
    }
    return slotNames[type] || type
  }

  // Resolver el slot objetivo a partir del tipo de item (para comparar desde inventario)
  const getTargetSlotFromItem = (it) => {
    if (!it || !it.type) return null
    const t = String(it.type).toLowerCase()

    if (['helmet', 'hat', 'headband'].includes(t)) return 'head'
    if (['armor', 'robe', 'shirt', 'chest'].includes(t)) return 'chest'
    if (['gloves', 'gauntlets', 'hands'].includes(t)) return 'hands'
    if (['pants', 'leggings', 'legs'].includes(t)) return 'legs'
    if (['boots', 'shoes', 'feet'].includes(t)) return 'feet'
    if (['shield', 'tome'].includes(t)) return 'offHand'
    if (['weapon', 'sword', 'axe', 'bow', 'staff'].includes(t)) return 'mainHand'
    // Consumibles u otros no comparan con equipo
    return null
  }

  const getStatComparison = () => {
    if (!item?.stats) return null

    // Determinar slot efectivo para comparar
    let effectiveSlot = slotType || null
    if (sourceType === 'inventory' && !effectiveSlot) {
      effectiveSlot = getTargetSlotFromItem(item)
    }

    // Encontrar el item equipado en el mismo slot para comparar
    let equippedItem = null
    if (effectiveSlot && equipment[effectiveSlot]) {
      equippedItem = equipment[effectiveSlot]
    }

    if (!equippedItem || !equippedItem.stats) return null

    const comparisons = []
    Object.keys(item.stats).forEach((statName) => {
      const newValue = item.stats[statName] || 0
      const currentValue = equippedItem.stats[statName] || 0
      const difference = newValue - currentValue

      if (difference !== 0) {
        comparisons.push({
          stat: statName,
          difference,
          isImprovement: difference > 0,
        })
      }
    })

    return comparisons
  }

  const statComparisons = getStatComparison()

  const tooltipStyle = {
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    zIndex: 1000,
    pointerEvents: 'none',
  }

  return (
    <div className="item-tooltip" style={tooltipStyle}>
      <div className="tooltip-content">
        {/* Header con imagen y nombre */}
        <div className="tooltip-header">
          <div className="tooltip-image">
            {item.image ? (
              <img src={item.image} alt={item.name} />
            ) : (
              <div className="tooltip-image-placeholder">
                <span>{item.name.charAt(0)}</span>
              </div>
            )}
          </div>
          <div className="tooltip-info">
            <h3 className="tooltip-name" style={{ color: getRarityColor(item.rarity) }}>
              {item.name}
            </h3>
            <p className="tooltip-rarity" style={{ color: getRarityColor(item.rarity) }}>
              {getRarityDisplayName(item.rarity)}
            </p>
            <p className="tooltip-type">{getSlotName(item.type)}</p>
            {Array.isArray(item.tags) && item.tags.includes('provision') && (
              <p className="tooltip-provision">Provisión de viaje</p>
            )}
          </div>
        </div>

        {/* Descripción */}
        {item.description && (
          <div className="tooltip-description">
            <p>{item.description}</p>
          </div>
        )}

        {/* Stats del item */}
        {item.stats && Object.keys(item.stats).length > 0 && (
          <div className="tooltip-stats">
            <h4>Estadísticas:</h4>
            <ul>
              {Object.entries(item.stats).map(([statName, value]) => (
                <li key={statName}>
                  <span className="stat-name">{getStatName(statName)}:</span>
                  <span className="stat-value">+{value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Comparación con item equipado */}
        {statComparisons && statComparisons.length > 0 && (
          <div className="tooltip-comparison">
            <h4>Comparación:</h4>
            <ul>
              {statComparisons.map(({ stat, difference, isImprovement }) => (
                <li key={stat} className={isImprovement ? 'improvement' : 'downgrade'}>
                  <span className="stat-name">{getStatName(stat)}:</span>
                  <span className="stat-difference">
                    {difference > 0 ? '+' : ''}
                    {difference}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Información adicional */}
        <div className="tooltip-additional">
          {item.damage && (
            <p>
              <strong>Daño:</strong> {item.damage}
            </p>
          )}
          {(item.grip === 'oneHand' || item.grip === 'twoHand' || item.handSlot) && (
            <p>
              <strong>Empuñadura:</strong>{' '}
              {item.grip === 'twoHand'
                ? 'Dos manos'
                : item.handSlot === 'mainHand'
                  ? 'Una mano (solo principal)'
                  : item.handSlot === 'offHand'
                    ? 'Una mano (solo secundaria)'
                    : 'Una mano'}
            </p>
          )}
          {item.isThrowable ? (
            <p>
              <strong>Arrojadizo:</strong> Sí
            </p>
          ) : null}
          {item.defense && (
            <p>
              <strong>Defensa:</strong> {item.defense}
            </p>
          )}
          {item.durability && (
            <p>
              <strong>Durabilidad:</strong> {item.durability.current}/{item.durability.max}
            </p>
          )}
          {item.weight && (
            <p>
              <strong>Peso:</strong> {item.weight}
            </p>
          )}
          {item.value && (
            <p>
              <strong>Valor:</strong> {item.value} oro
            </p>
          )}
        </div>

        {/* Cantidad para items apilables */}
        {item.quantity > 1 && (
          <div className="tooltip-quantity">
            <p>
              <strong>Cantidad:</strong> {item.quantity}
            </p>
          </div>
        )}

        {/* Requisitos */}
        {item.requirements && (
          <div className="tooltip-requirements">
            <h4>Requisitos:</h4>
            <ul>
              {Object.entries(item.requirements).map(([req, value]) => {
                const meetsRequirement = character.stats[req] && character.stats[req].base + character.stats[req].bonus >= value
                return (
                  <li key={req} className={meetsRequirement ? 'requirement-met' : 'requirement-not-met'}>
                    <span>
                      {getStatName(req)}: {value}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

// Helper function para obtener nombres de stats en español
const getStatName = (statKey) => {
  const statNames = {
    strength: 'Fuerza',
    dexterity: 'Destreza',
    agility: 'Agilidad',
    resistance: 'Resistencia',
    willpower: 'Voluntad',
    chakraControl: 'Control de Chakra',
    concentration: 'Concentración',
    intelligence: 'Inteligencia',
    damage: 'Daño',
    defense: 'Defensa',
    critical: 'Crítico',
    accuracy: 'Precisión',
    evasion: 'Evasión',
  }
  return statNames[statKey] || statKey
}

export default ItemTooltip
